import type { SlideDirection } from '@core/transitions/types';
import { getComputedTranslate } from '@core/utils';

export type TransitionTimeout = number | { enter?: number; exit?: number };

/**
 * Resolve a single duration (ms) from a transition timeout value and mode.
 * Use for components that accept timeout as number or { enter?, exit? }.
 */
export function getTransitionTimeout(
  value: TransitionTimeout | undefined,
  mode: 'enter' | 'exit',
  defaults: { enter: number; exit: number }
): number {
  if (typeof value === 'number') return value;
  if (value && typeof value === 'object') {
    const v = value[mode];
    return typeof v === 'number' ? v : defaults[mode];
  }
  return defaults[mode];
}

/**
 * Translate the node off-screen (viewport-based). Matches MUI Slide behavior.
 */
function getTranslateValue(
  direction: SlideDirection,
  node: HTMLElement
): string {
  const rect = node.getBoundingClientRect();
  const { x: offsetX, y: offsetY } = getComputedTranslate(node);

  switch (direction) {
    case 'left':
      return `translateX(${window.innerWidth}px) translateX(${
        offsetX - rect.left
      }px)`;
    case 'right':
      return `translateX(-${rect.left + rect.width - offsetX}px)`;
    case 'up':
      return `translateY(${window.innerHeight}px) translateY(${
        offsetY - rect.top
      }px)`;
    case 'down':
    default:
      return `translateY(-${rect.top + rect.height - offsetY}px)`;
  }
}

export function setTranslateValue(
  direction: SlideDirection,
  node: HTMLElement | null
): void {
  if (!node) return;
  const value = getTranslateValue(direction, node);
  node.style.transform = value;
}
